import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { getMyMealMenus } from '../../services/myMealService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';

const Wrapper = styled.div``;

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 16px; margin-bottom: 12px;
`;

const WeekLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const DayCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 14px;
  min-height: 200px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const DayHeader = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; color: #64748b;
  text-transform: none;
  margin-bottom: 6px;
`;

const DayDate = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
`;

const MealBlock = styled.div`
  padding: 8px 0;
  border-top: 1px solid #f1f5f9;
  &:first-of-type { border-top: none; }
`;

const MealName = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  margin-bottom: 3px;
`;

const MealItems = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; color: #475569;
  line-height: 1.4;
`;

const Empty = styled.div`
  color: #A0A8B4;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-style: italic;
`;

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function startOfWeek(d) {
  const copy = new Date(d);
  const day = (copy.getDay() + 6) % 7; // Monday = 0
  copy.setDate(copy.getDate() - day);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

const DAY_HEADERS = {
  tr: ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'],
  en: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'],
  ru: ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'],
  kk: ['Дс','Сс','Ср','Бс','Жм','Сн','Жс'],
  az: ['B.e','Ç.a','Çər','C.a','Cüm','Şən','Baz'],
};

export default function MyMealsWeeklyPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'tr';

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  useEffect(() => {
    setLoading(true);
    const from = isoDate(days[0]);
    const to = isoDate(days[6]);
    getMyMealMenus(from, to)
      .then((r) => {
        const result = r.data?.data;
        setMenus(Array.isArray(result) ? result : (result?.data || []));
      })
      .catch(() => setMenus([]))
      .finally(() => setLoading(false));
  }, [days]);

  const menusByDate = useMemo(() => {
    const m = {};
    menus.forEach((x) => {
      if (!m[x.menuDate]) m[x.menuDate] = [];
      m[x.menuDate].push(x);
    });
    return m;
  }, [menus]);

  const prevWeek = useCallback(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  }, [weekStart]);

  const nextWeek = useCallback(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  }, [weekStart]);

  const weekLabel = useMemo(() => {
    const from = days[0].toLocaleDateString(lang, { day: 'numeric', month: 'short' });
    const to = days[6].toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
    return `${from} — ${to}`;
  }, [days, lang]);

  const dayHeaders = DAY_HEADERS[lang] || DAY_HEADERS.tr;

  if (loading && menus.length === 0) {
    // First load — show layout with skeleton later if needed
  }

  return (
    <Wrapper>
      <Breadcrumbs items={[
        { label: t('canteen.myMeals'), path: '/my-meals' },
        { label: t('canteen.weeklyMenu') },
      ]} />

      <Header>
        <Button variant="ghost" onClick={prevWeek}>
          <ChevronLeft size={14} /> {t('canteen.previousWeek')}
        </Button>
        <WeekLabel>{weekLabel}</WeekLabel>
        <Button variant="ghost" onClick={nextWeek}>
          {t('canteen.nextWeek')} <ChevronRight size={14} />
        </Button>
      </Header>

      <Grid>
        {days.map((d, idx) => {
          const key = isoDate(d);
          const dayMenus = menusByDate[key] || [];
          return (
            <DayCard key={key}>
              <DayHeader>{dayHeaders[idx]}</DayHeader>
              <DayDate>{d.getDate()} {d.toLocaleDateString(lang, { month: 'short' })}</DayDate>
              {dayMenus.length === 0
                ? <Empty>{t('canteen.noMenuForToday')}</Empty>
                : dayMenus.map((m) => (
                    <MealBlock key={m.id}>
                      <MealName>{m.mealName}</MealName>
                      <MealItems>{(m.items || []).join(', ')}</MealItems>
                    </MealBlock>
                  ))
              }
            </DayCard>
          );
        })}
      </Grid>
    </Wrapper>
  );
}
